import {
  FLUSH,
  PAUSE,
  PERSIST,
  PersistConfig,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { combineReducers, configureStore as reduxConfigureStore } from '@reduxjs/toolkit';

import appSlice from './features/appSlice.js';

export const rootReducer = combineReducers({
  app: appSlice.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

export type ConfigureStoreOptions = PersistConfig<RootState>;

export const configureStore = <S extends RootState>(
  preloadedState?: S,
  options?: Partial<ConfigureStoreOptions>
) => {
  const defaultPersistConfig: PersistConfig<RootState> = {
    ...options,
    key: 'root',
    version: 1,
    storage: storage,
    transforms: [],
  };

  const persistedReducer = persistReducer(defaultPersistConfig, rootReducer);

  const store = reduxConfigureStore({
    devTools: import.meta.env.DEV,
    reducer: persistedReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      // https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }),
  });

  const persistor = persistStore(store);

  return {
    ...store,
    persistor,
  };
};

export type Store = ReturnType<typeof configureStore>;
export type StoreDispatch = Store['dispatch'];
export type StoreRootState = ReturnType<Store['getState']>;

export * from './features/appSlice.js';

export const actions = {
  ...appSlice.actions,
};

export const selectRoot = (state: StoreRootState) => state;
export const selectApp = (state: StoreRootState) => state.app;

export default configureStore;
